import { AsyncPipe, CurrencyPipe, DecimalPipe, Location, UpperCasePipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { Cta, DiscoverFacade } from '@zero/discover/domain';
import { MarkdownPipe } from '@zero/discover/feature-discover/web';
import { BlinkDirective } from '@zero/shared/ui-directives';
import { IncludesPipe } from '@zero/shared/ui-pipes';
import { InstrumentDetailsLinkComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { filter, map, switchMap, take } from 'rxjs';

@Component({
  imports: [
    AsyncPipe,
    CurrencyPipe,
    BlinkDirective,
    TranslocoPipe,
    IncludesPipe,
    InstrumentDetailsLinkComponent,
    DecimalPipe,
    MarkdownPipe,
    ButtonComponent,
    UpperCasePipe,
  ],
  templateUrl: './multi-order-strategies-details-page.component.html',
  styleUrl: './multi-order-strategies-details-page.component.scss',
})
export class MultiOrderStrategiesDetailsPageComponent implements OnInit, OnDestroy {
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);

  public readonly imageBase = environment.assetshost;
  public readonly facade = inject(DiscoverFacade);
  public readonly location = inject(Location);

  public hasCta$ = this.facade.multiOrderStrategy$.pipe(
    filter((strategy) => !!strategy),
    map((strategy) => strategy?.cta?.text && (strategy?.cta?.link || strategy?.cta?.externalLink)),
  );
  public showCheckbox$ = this.hasCta$.pipe(map((hasCta) => !hasCta));

  public isins: string[] = [];

  ngOnInit(): void {
    this.#listenToUrlParam();
    this.#initCheckedItems();
  }

  ngOnDestroy(): void {
    this.facade.clearMultiOrderStrategy();
  }

  public toggle = (event: Event, isin: string) =>
    (this.isins = (event.currentTarget as HTMLInputElement).checked ? [...this.isins, isin] : this.isins.filter((item) => item !== isin));

  #listenToUrlParam = () => this.#route.params.subscribe((params) => this.facade.loadMultiOrderStrategy(params['id']));

  #initCheckedItems = () =>
    this.facade.multiOrderStrategyAssetsLoaded$
      .pipe(
        filter((multiOrderStrategyAssetsLoaded) => !!multiOrderStrategyAssetsLoaded),
        switchMap(() => this.facade.multiOrderStrategy$),
        filter((strategy) => Boolean(strategy?.assets?.length)),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((strategy) => (this.isins = strategy.assets.map((item) => item.isin)));

  public handleCtaButtonClick(cta: Cta) {
    if (cta.link) {
      this.#router.navigateByUrl(cta.link);
    } else if (cta.externalLink) {
      window.open(cta.externalLink, '_blank');
    }
  }

  public handleMultiorderButtonClick(strategyId: string) {
    this.#router.navigate([commonRoutes.postlogin.multiOrder], {
      queryParams: {
        isin: this.isins.join(','),
        fzterm: this.isins,
        fzcontent: 'button',
        ...this.#getGeneralTrackingParams(strategyId),
      },
    });
  }

  public handleBuyButtonClick(strategyId: string, isin: string, index: number) {
    this.#router.navigate([commonRoutes.postlogin.buyAndSell], {
      queryParams: {
        isin,
        direction: 'buy',
        fzterm: isin,
        fzcontent: `instrumentlist-position-${++index}`,
        ...this.#getGeneralTrackingParams(strategyId),
      },
    });
  }

  #getGeneralTrackingParams(strategyId: string) {
    return {
      fzsrc: 'zerocockpit',
      fzcampaign: `multiorder-${strategyId}`,
      fzmedium: 'web',
    };
  }
}
